import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WifiHigh, ArrowRight, Terminal, ShieldCheck, UserGear } from "@phosphor-icons/react";

export default function Login() {
  const { user, login, staffLogin } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("admin"); // admin | staff
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "staff") {
        await staffLogin(email.trim().toLowerCase(), password);
        toast.success("Staff session established");
      } else {
        await login(email.trim().toLowerCase(), password);
        toast.success("Admin session established");
      }
      nav("/", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 grain relative">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between border-r border-border p-10 overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
          maskImage: "radial-gradient(ellipse at 30% 40%, black 30%, transparent 75%)",
        }} />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 border border-primary/60 grid place-items-center bg-primary/10">
            <WifiHigh size={20} weight="bold" className="text-primary" />
          </div>
          <div>
            <div className="font-display font-bold text-lg leading-none">SNS RADIUS</div>
            <div className="uppercase-label mt-1">ISP OPERATIONS CONSOLE</div>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display font-bold text-5xl leading-[0.95]">
            Run your ISP<br />
            <span className="text-primary">like a NOC.</span>
          </h1>
          <p className="text-muted-foreground max-w-md text-sm leading-relaxed">
            Subscribers, billing, OLT inventory, RADIUS sessions and live client-status
            on a map — one dark console for engineers who like their tools tight.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Terminal size={14} /> v0.2.0 · dark-mode-only
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up" data-testid="login-form">
          <div>
            <div className="uppercase-label">Authenticate</div>
            <h2 className="font-display text-3xl font-bold mt-2">Sign in to console</h2>
            <p className="text-sm text-muted-foreground mt-1">Master admin & NAS staff.</p>
          </div>

          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-1 border border-border p-1 rounded-sm bg-secondary/30" data-testid="login-mode-toggle">
            {[
              { k: "admin", label: "Master Admin", icon: ShieldCheck },
              { k: "staff", label: "NAS Employee", icon: UserGear },
            ].map((m) => (
              <button
                key={m.k}
                type="button"
                onClick={() => setMode(m.k)}
                data-testid={`login-mode-${m.k}`}
                className={`flex items-center justify-center gap-1.5 py-2 text-xs uppercase tracking-wider font-mono rounded-sm transition-colors ${mode === m.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              >
                <m.icon size={12} /> {m.label}
              </button>
            ))}
          </div>

          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="uppercase-label">Email</Label>
              <Input id="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="mt-1.5 bg-secondary border-border font-mono" />
            </div>
            <div>
              <Label htmlFor="password" className="uppercase-label">Password</Label>
              <Input id="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="mt-1.5 bg-secondary border-border font-mono" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground font-medium h-11 group">
            {loading ? "Authenticating…" : (<>Enter console <ArrowRight size={16} weight="bold" className="ml-2 group-hover:translate-x-0.5 transition-transform" /></>)}
          </Button>
        </form>
      </div>
    </div>
  );
}
